import { Component } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { environment } from 'src/environments/environment';
import { AuthService } from '../../services/auth.service';
import { TranslatePipe } from '../../i18n/translate.pipe';

/** Powód, dla którego strażnik trasy nie wpuścił dalej. */
export type NotAuthorizedReason = 'private' | 'signin' | 'admin' | 'tree';

/**
 * Ekran „nie tędy droga". Pokazuje się ZAMIAST strony, na którą użytkownik
 * wszedł (strażnicy przekierowują tu z `skipLocationChange`), więc adres w
 * pasku zostaje ten pierwotny - odświeżenie po zalogowaniu prowadzi tam,
 * gdzie użytkownik chciał trafić.
 */
@Component({
    selector: 'app-not-authorized',
    templateUrl: './not-authorized.component.html',
    styleUrls: ['./not-authorized.component.css'],
    standalone: true,
    imports: [RouterLink, MatButtonModule, MatIconModule, TranslatePipe]
})
export class NotAuthorizedComponent {

  /** Bez parametru zakładamy najstarszy przypadek - prywatne drzewo. */
  readonly reason: NotAuthorizedReason;
  /** Adres, na który użytkownik chciał wejść - wracamy tam po zalogowaniu. */
  private readonly from: string;

  constructor(private route: ActivatedRoute, private router: Router,
    private authService: AuthService) {
    const params = this.route.snapshot.queryParamMap;
    // Powód bierzemy z adresu, a gdy go tam nie ma - z definicji trasy: ekran
    // logowania do drzewa ma własny adres (/tree/{drzewo}/login), więc nie ma
    // gdzie doklejać parametru.
    const reason = params.get('reason') ?? this.route.snapshot.data['reason'];
    this.reason = reason === 'signin' || reason === 'admin' || reason === 'tree'
      ? reason : 'private';
    this.from = params.get('from') ?? '';
  }

  get icon(): string {
    return this.reason === 'signin' || this.reason === 'tree' ? 'login' : 'lock';
  }

  get titleKey(): string {
    switch (this.reason) {
      case 'signin': return 'notAuthorized.signinTitle';
      case 'tree': return 'notAuthorized.treeTitle';
      case 'admin': return 'notAuthorized.adminTitle';
      default: return 'notAuthorized.privateTitle';
    }
  }

  get messageKey(): string {
    switch (this.reason) {
      case 'signin': return 'notAuthorized.signinText';
      case 'tree': return 'notAuthorized.treeText';
      case 'admin': return 'notAuthorized.adminText';
      default: return 'notAuthorized.privateText';
    }
  }

  /** Adres drzewa, o ile w ogóle wiemy, o które chodzi. */
  get treeUrl(): string {
    return environment.currentTreeUrl;
  }

  get treeTitle(): string {
    return environment.currentTreeDescription;
  }

  /**
   * Do drzewa wraca tylko ten, kto faktycznie może je oglądać. Gość przy
   * drzewie „dla zalogowanych" nie może - odesłalibyśmy go z powrotem tutaj.
   */
  get canGoToTree(): boolean {
    return !!this.treeUrl && this.reason !== 'private' && this.reason !== 'tree';
  }

  /** Zalogowanemu użytkownikowi bez uprawnień logowanie niczego nie zmieni. */
  get showSignIn(): boolean {
    return this.reason !== 'admin';
  }

  /**
   * Logowanie z zapamiętaniem celu. Bez tego wracaliśmy na tę stronę -
   * strażnik przekierowywał tu z podmianą adresu, więc „wróć tam, gdzie
   * byłeś" oznaczało „wróć na komunikat o braku dostępu".
   */
  signIn() {
    if (this.from) {
      this.authService.setRedirectUrl(this.from);
    }
    this.router.navigate(['/signin']);
  }
}
